/**
 * AdminAuthContext
 *
 * Manages Cognito authentication state for the admin page.
 * Uses AWS Amplify v6 for token management and refresh.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import { Amplify } from 'aws-amplify'
import {
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
  type SignInOutput,
} from 'aws-amplify/auth'

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_ADMIN_COGNITO_USER_POOL_ID ?? '',
      userPoolClientId: import.meta.env.VITE_ADMIN_COGNITO_CLIENT_ID ?? '',
    },
  },
})

interface AdminAuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  accessToken: string | null
  login: (email: string, password: string) => Promise<SignInOutput>
  logout: () => Promise<void>
  refreshSession: () => Promise<void>
}

const AdminAuthContext = createContext<AdminAuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  accessToken: null,
  login: async () => { throw new Error('Not initialized') },
  logout: async () => {},
  refreshSession: async () => {},
})

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [accessToken, setAccessToken] = useState<string | null>(null)

  const refreshSession = useCallback(async () => {
    try {
      await getCurrentUser()
      const session = await fetchAuthSession()
      const token = session.tokens?.accessToken?.toString() ?? null
      if (token) {
        setAccessToken(token)
        setIsAuthenticated(true)
      } else {
        // Session exists but no valid token — clear it
        await signOut()
        setAccessToken(null)
        setIsAuthenticated(false)
      }
    } catch {
      setAccessToken(null)
      setIsAuthenticated(false)
    }
  }, [])

  // Check for existing session on mount
  useEffect(() => {
    async function checkSession() {
      try {
        await getCurrentUser()
        const session = await fetchAuthSession()
        const token = session.tokens?.accessToken?.toString() ?? null
        if (token) {
          setAccessToken(token)
          setIsAuthenticated(true)
        } else {
          // Stale/partial session (e.g. pending challenge after page refresh) — clear it
          await signOut()
          setAccessToken(null)
          setIsAuthenticated(false)
        }
      } catch {
        setAccessToken(null)
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }
    checkSession()
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<SignInOutput> => {
    try {
      const result = await signIn({ username: email, password })
      if (result.isSignedIn) {
        const session = await fetchAuthSession()
        const token = session.tokens?.accessToken?.toString() ?? null
        setAccessToken(token)
        setIsAuthenticated(true)
      }
      return result
    } catch (err: unknown) {
      // If a stale session is blocking a new sign-in, clear it and retry once
      const msg = err instanceof Error ? err.message : ''
      if (msg.includes('There is already a signed in user')) {
        await signOut()
        const result = await signIn({ username: email, password })
        if (result.isSignedIn) {
          const session = await fetchAuthSession()
          const token = session.tokens?.accessToken?.toString() ?? null
          setAccessToken(token)
          setIsAuthenticated(true)
        }
        return result
      }
      throw err
    }
  }, [])

  const logout = useCallback(async () => {
    await signOut()
    setAccessToken(null)
    setIsAuthenticated(false)
  }, [])

  return (
    <AdminAuthContext.Provider value={{ isAuthenticated, isLoading, accessToken, login, logout, refreshSession }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth(): AdminAuthContextType {
  return useContext(AdminAuthContext)
}
